/* Generic component to display all asset types - download declarations, upload declarations, keys */
import { List, ListItem, ListItemText, ListSubheader, Typography, styled } from "@mui/material";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import React, { ReactNode } from "react";
import { CREDENTIAL_TYPES_BY_NAME } from "../KeyEditor";

export type AssetPanelItem = {
  name: string;
  parentAsset?: { name: string; declarationType: "import" | "export" };
  testing?: boolean;
};
interface IAssetPanel {
  assetMap: Map<string, Array<AssetPanelItem>>;
  mode: string;
}

const ParentAsset = styled("div")(({ theme }) => ({
  color: theme.palette.text30.main,
  fontSize: "0.9em",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
}));

const AssetItemText = styled(ListItemText)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingLeft: theme.spacing(1),
}));

const Panel = styled("div")({
  width: "100%",
});

const ListSubheaderBold = styled(ListSubheader)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: "14px",
  color: theme.palette.text50.main,
}));

const AssetPanel: React.FC<IAssetPanel> = ({ assetMap, mode }) => {
  const assetNodes: Array<ReactNode> = [];

  const parentAssetIconTypes = {
    import: <CloudArrowDownIcon style={{ marginRight: 8 }} />,
    export: <CloudArrowUpIcon style={{ marginRight: 8 }} />,
  };
  if (assetMap.size) {
    for (const [type, assets] of assetMap) {
      assetNodes.push(
        <React.Fragment key={type}>
          <ListSubheaderBold>{CREDENTIAL_TYPES_BY_NAME[type]?.display_name}</ListSubheaderBold>
          {assets.map((asset, idx) => (
            <React.Fragment key={idx}>
              <ListItem secondaryAction={asset.testing ? <FlaskIcon /> : null} dense>
                <AssetItemText
                  slotProps={{
                    primary: {
                      style: {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        fontSize: "12px",
                      },
                    },
                  }}
                >
                  {asset.name}
                  {asset.parentAsset ? (
                    <ParentAsset>
                      {parentAssetIconTypes[asset.parentAsset.declarationType]}
                      {asset.parentAsset.name}
                    </ParentAsset>
                  ) : null}
                </AssetItemText>
              </ListItem>
            </React.Fragment>
          ))}
        </React.Fragment>
      );
    }
  }

  return (
    <Panel>
      <List>
        <Typography data-cy={`${mode}-title`} sx={{ fontSize: "1.5rem", pl: 2, pr: 2 }}>
          {mode}
        </Typography>
        {assetNodes}
      </List>
    </Panel>
  );
};

export default AssetPanel;
